'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { IconButton } from '@hrworks/sui-core/IconButton';
import { MaterialDesignIcon } from '@hrworks/sui-core/MaterialDesignIcon';

import { S, slideFromLeft, slideFromRight } from './Lightbox.styles';
import type { LightboxProps } from './Lightbox.types';

export const Lightbox = ({ images, youtubeEmbedUrl, initialIndex = 0, open, onCloseAction }: LightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const directionRef = useRef<'left' | 'right' | null>(null);
  const [animKey, setAnimKey] = useState(0);

  const hasVideo = !!youtubeEmbedUrl;
  const totalItems = images.length + (hasVideo ? 1 : 0);
  const videoIndex = hasVideo ? 0 : -1;
  const imageOffset = hasVideo ? 1 : 0;

  useEffect(() => {
    setCurrentIndex(initialIndex);
    directionRef.current = null;
  }, [initialIndex]);

  const goNext = useCallback(() => {
    directionRef.current = 'right';
    setCurrentIndex((i) => (i + 1) % totalItems);
    setAnimKey((k) => k + 1);
  }, [totalItems]);

  const goPrev = useCallback(() => {
    directionRef.current = 'left';
    setCurrentIndex((i) => (i - 1 + totalItems) % totalItems);
    setAnimKey((k) => k + 1);
  }, [totalItems]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseAction();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onCloseAction, goNext, goPrev]);

  if (!open || totalItems === 0) return null;

  return (
    <S.Overlay onClick={onCloseAction} role="dialog" aria-modal="true" aria-label="Image lightbox">
      <S.CloseButton>
        <IconButton onClick={onCloseAction} aria-label="Close" color="secondary">
          <MaterialDesignIcon name="close" />
        </IconButton>
      </S.CloseButton>
      {totalItems > 1 && (
        <S.PrevButton>
          <IconButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous"
            color="secondary"
          >
            <MaterialDesignIcon name="arrow_downward" css={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </S.PrevButton>
      )}
      <S.ImageContainer onClick={(e) => e.stopPropagation()}>
        {currentIndex === videoIndex && youtubeEmbedUrl ? (
          <iframe
            key={animKey}
            src={`${youtubeEmbedUrl}?autoplay=1`}
            title="Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            css={{ width: '80vw', height: '45vw', maxHeight: '80vh', border: 'none' }}
          />
        ) : (
          // biome-ignore lint/performance/noImgElement: customer-supplied S3/CloudFront URLs — next/image throws for domains not listed in remotePatterns
          <img
            key={animKey}
            src={images[currentIndex - imageOffset]}
            alt=""
            css={
              directionRef.current === 'right'
                ? slideFromRight
                : directionRef.current === 'left'
                  ? slideFromLeft
                  : undefined
            }
          />
        )}
      </S.ImageContainer>
      {totalItems > 1 && (
        <S.NextButton>
          <IconButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next"
            color="secondary"
          >
            <MaterialDesignIcon name="arrow_downward" css={{ transform: 'rotate(-90deg)' }} />
          </IconButton>
        </S.NextButton>
      )}
    </S.Overlay>
  );
};
